/**
 * tepo-design-capture.js
 *
 * Captures the artwork image URL of the design selected in the qikify / tepo
 * Product Options widget and stamps it onto the cart line item as a hidden
 * line-item property (`_front_design_image` / `_back_design_image`).
 *
 * WHY: the tepo app only writes the design NAME to the cart; the artwork URL
 * lives only in the in-page `window._TC` data on the PDP. By copying the URL
 * onto the line item we make the image travel with the line so it can render
 * in the cart, drawer and (future) checkout with no runtime tepo dependency.
 *
 * Only lines carrying a DESIGN_KEYS property are touched. Do not widen this to
 * match on other properties: patchCart rewrites the line via cart/change.js,
 * which replaces the whole property set with what /cart.js returned, and
 * /cart.js withholds Qikify's `_pricePerLetterTotalCharge`. Rewriting a line
 * that carries it drops the per-character surcharge from its price.
 *
 * Robustness:
 *  - The name->image map is persisted to localStorage on the PDP, so the cart
 *    page can resolve images even when tepo adds via a full-page navigation
 *    (where the PDP-time fetch/XHR interception never runs).
 *  - Loaded site-wide; self-guards and no-ops when there is no usable map or no
 *    matching cart line.
 *
 * Verbose logging is gated behind window.__TEPO_DESIGN_DEBUG (on by default
 * while validating; set to false to silence).
 */
(function () {
  'use strict';

  var FRONT_IMG_KEY = '_front_design_image';
  var BACK_IMG_KEY = '_back_design_image';
  // Keep in sync with snippets/line-item.liquid: it reads exactly these two to
  // decide whether a line renders a design thumbnail.
  var DESIGN_KEYS = ['Elige tu diseño frontal', 'Elige tu diseño trasero'];
  var ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  var STORAGE_KEY = 'tepoDesignImageMap_v2';
  var processing = false;

  if (window.__TEPO_DESIGN_DEBUG === undefined) window.__TEPO_DESIGN_DEBUG = true;
  function log() {
    if (!window.__TEPO_DESIGN_DEBUG) return;
    console.log.apply(console, ['[tepo-design]'].concat([].slice.call(arguments)));
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /** Read a previously-persisted name->{url,side} map from localStorage. */
  function readStored() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  }

  /** Persist the name->{url,side} map so other pages (cart) can resolve images. */
  function persist(byName) {
    try {
      var existing = readStored();
      var merged = Object.assign({}, existing, byName);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    } catch (e) { /* storage may be unavailable */ }
  }

  /**
   * Build name->{url,side} from tepo's in-page option data (window._TC).
   * Falls back to the persisted localStorage map when _TC is absent (e.g. on
   * the cart page). `side` is 'front' | 'back' inferred from the field label.
   */
  function buildMaps() {
    var byName = {};
    var counts = { front: 0, back: 0 };
    var source = 'none';
    try {
      var TC = window._TC;
      if (TC && TC.manualOptions) {
        source = '_TC';
        Object.keys(TC.manualOptions).forEach(function (pid) {
          var fields = TC.manualOptions[pid];
          if (!Array.isArray(fields)) fields = fields && (fields.fields || fields.options);
          if (!Array.isArray(fields)) return;
          fields.forEach(function (f) {
            if (!f || f.type !== 'RadioImage' || !Array.isArray(f.options)) return;
            var label = (f.label || '').toLowerCase();
            // Skip the side/serigrafía selector — its options ("FRONTAL"/"TRASERA")
            // are NOT designs and would otherwise collide with the side property value.
            if (label.indexOf('serigraf') !== -1 || label.indexOf('lado') !== -1) return;
            var side = 'front';
            if (label.indexOf('trasero') !== -1 || label.indexOf('trasera') !== -1) side = 'back';
            f.options.forEach(function (o) {
              if (o && o.name && o.image) {
                byName[String(o.name).trim()] = { url: 'https://cdn.tepo.app/' + o.image, side: side };
                counts[side]++;
              }
            });
          });
        });
        if (Object.keys(byName).length) persist(byName);
      }
    } catch (e) { log('buildMaps error', e); }

    if (!Object.keys(byName).length) {
      byName = readStored();
      if (Object.keys(byName).length) source = 'localStorage';
      Object.keys(byName).forEach(function (n) { counts[byName[n].side === 'back' ? 'back' : 'front']++; });
    }
    window.__tepoDesignMaps = byName;
    return { byName: byName, counts: counts, source: source };
  }

  function sectionsToRender() {
    var list = [];
    var mainCartEl = document.getElementById('main-cart-items');
    var mainCartId = mainCartEl && mainCartEl.dataset.id;
    if (mainCartId) {
      list.push({ id: '#shopify-section-' + mainCartId, section: mainCartId, selector: '#shopify-section-' + mainCartId + ' cart-items' });
    }
    if (document.querySelector('#shopify-section-cart-counter')) {
      list.push({ id: '#cart-counter', section: 'cart-counter', selector: '#shopify-section-cart-counter' });
    }
    if (document.querySelector('cart-drawer')) {
      list.push({ id: '#CartDrawer-Body', section: 'cart-drawer', selector: '#shopify-section-cart-drawer #CartDrawer-Body' });
    }
    return list;
  }

  function refreshSections() {
    var toRender = sectionsToRender();
    if (!toRender.length) { log('no cart sections on this page to refresh'); return Promise.resolve(); }
    var ids = toRender.map(function (s) { return s.section; }).join(',');
    return fetch(root() + '?sections=' + encodeURIComponent(ids))
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        toRender.forEach(function (s) {
          if (!sections[s.section]) return;
          var el = document.querySelector(s.selector) || document.querySelector(s.id);
          if (!el) return;
          try {
            var parsed = new DOMParser().parseFromString(sections[s.section], 'text/html').querySelector(s.selector);
            if (parsed) { el.innerHTML = parsed.innerHTML; log('refreshed section', s.section); }
          } catch (e) { log('refresh parse error', s.section, e); }
        });
        scheduleSwap();
      })
      .catch(function (e) { log('refreshSections error', e); });
  }

  /**
   * Instantly set design thumbnails from the cached name->url map (no network),
   * keyed off the data-tepo-design-name attribute line-item.liquid emits. This
   * removes the default-image flash: the swap happens the frame the cart renders,
   * before the server-side property is persisted via change.js.
   */
  function reveal(img) {
    var media = (img.closest && img.closest('.cart-item__design-media')) || img.parentNode;
    if (media && media.classList) media.classList.add('is-loaded');
  }

  function swapDom() {
    var maps = (window.__tepoDesignMaps && Object.keys(window.__tepoDesignMaps).length) ? window.__tepoDesignMaps : readStored();
    var hasMap = maps && Object.keys(maps).length;
    var imgs = document.querySelectorAll('img.cart-item__design-img');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      // Resolve a missing src from the cached map (instant, no network). Done on
      // every pass (not gated by the bind-once flag) in case the map only became
      // available after the first pass.
      if (!img.getAttribute('src') && hasMap) {
        var a = img.closest && img.closest('a.cart-item__link');
        var name = a && a.getAttribute('data-tepo-design-name');
        var hit = name ? (maps[name] || maps[name.trim()]) : null;
        if (hit) {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = hit.url + (hit.url.indexOf('?') === -1 ? '?w=600' : '');
        }
      }
      // Hide the skeleton once the image is ready (bind handlers once).
      if (img.complete && img.naturalWidth > 0) { reveal(img); }
      if (!img.__tepoBound) {
        img.__tepoBound = true;
        img.addEventListener('load', function () { reveal(this); });
        img.addEventListener('error', function () { reveal(this); }); // avoid a stuck skeleton
      }
    }
  }

  var swapScheduled = false;
  function scheduleSwap() {
    if (swapScheduled) return;
    swapScheduled = true;
    (window.requestAnimationFrame || function (cb) { setTimeout(cb, 16); })(function () {
      swapScheduled = false;
      swapDom();
    });
  }

  /** Watch cart containers so we swap instantly whenever the theme re-renders them. */
  function observeCarts() {
    if (!('MutationObserver' in window)) return;
    var obs = new MutationObserver(scheduleSwap);
    var targets = [];
    var drawer = document.querySelector('cart-drawer');
    if (drawer) targets.push(drawer);
    var mainCart = document.getElementById('main-cart-items');
    if (mainCart) targets.push(mainCart);
    if (!targets.length) targets.push(document.body);
    targets.forEach(function (t) { obs.observe(t, { childList: true, subtree: true }); });
    log('observing', targets.length, 'cart container(s) for instant swap');
  }

  function patchCart(reason, force) {
    if (processing) { return Promise.resolve(); }
    var maps = buildMaps();
    log('patchCart (' + (reason || '') + ') — names:', Object.keys(maps.byName).length, 'source:', maps.source, maps.counts);
    if (!Object.keys(maps.byName).length) return Promise.resolve();
    processing = true;

    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        log('cart has', (cart.items || []).length, 'item(s)');
        var chain = Promise.resolve();
        var didChange = false;
        var designPresent = false;
        (cart.items || []).forEach(function (it, idx) {
          var props = it.properties || {};
          var keys = Object.keys(props);
          log('item[' + idx + ']', it.product_title || it.title, '— property keys:', JSON.stringify(keys));
          if (props[FRONT_IMG_KEY] || props[BACK_IMG_KEY]) { designPresent = true; log('item[' + idx + '] already has design image'); return; }
          var matched = null;
          for (var i = 0; i < keys.length; i++) {
            var k = keys[i];
            if (DESIGN_KEYS.indexOf(k) === -1) continue;
            var v = props[k];
            if (typeof v !== 'string') continue;
            var hit = maps.byName[v] || maps.byName[v.trim()];
            if (hit) { matched = { key: k, value: v, url: hit.url, side: hit.side }; break; }
          }
          if (!matched) { log('item[' + idx + '] — no design property on this line'); return; }
          var imgKey = matched.side === 'back' ? BACK_IMG_KEY : FRONT_IMG_KEY;
          log('item[' + idx + '] MATCH', matched.key, '=', matched.value, '->', matched.url, '(' + imgKey + ')');
          var newProps = Object.assign({}, props);
          newProps[imgKey] = matched.url;
          didChange = true;
          designPresent = true;
          chain = chain.then(function () {
            return fetch(root() + 'cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: it.key, quantity: it.quantity, properties: newProps })
            })
              .then(function (r) { log('change.js', r.status, 'item[' + idx + ']'); return r.json().catch(function () { return null; }); })
              .catch(function (e) { log('change.js error', e); });
          });
        });
        return chain.then(function () {
          // Refresh when we just wrote an image, or on a forced pass when a design
          // line exists — the latter overwrites the stale drawer the theme renders
          // (cart.renderContents) right after add, before our capture completed.
          if (didChange || (force && designPresent)) {
            log('refreshing cart UI (didChange=' + didChange + ', force=' + !!force + ')');
            return refreshSections();
          }
          return null;
        });
      })
      .catch(function (e) { log('patchCart error', e); })
      .then(function () { processing = false; });
  }

  function schedulePatch(reason) {
    setTimeout(function () { patchCart(reason + '@250', false); }, 250);
    // Later forced passes re-render the drawer to beat the theme/tepo opening it
    // with the stale (pre-capture) line after our first refresh.
    [1000, 2200, 3500].forEach(function (ms) { setTimeout(function () { patchCart(reason + '@' + ms, true); }, ms); });
  }

  // --- Intercept add-to-cart (fetch) ---
  var origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isAdd = ADD_RE.test(url);
      var p = origFetch.apply(this, arguments);
      if (isAdd) {
        log('intercepted fetch /cart/add ->', url);
        if (p && typeof p.then === 'function') p.then(function () { schedulePatch('fetch'); }).catch(function () {});
      }
      return p;
    };
  }

  // --- Intercept add-to-cart (XHR) ---
  var origOpen = XMLHttpRequest.prototype.open;
  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    try { this.__tepoIsAdd = ADD_RE.test(url || ''); } catch (e) { this.__tepoIsAdd = false; }
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__tepoIsAdd) { log('intercepted XHR /cart/add'); this.addEventListener('load', function () { schedulePatch('xhr'); }); }
    return origSend.apply(this, arguments);
  };

  // Manual trigger for console debugging.
  window.__tepoDesignPatch = function () { return patchCart('manual'); };

  function init() {
    var maps = buildMaps();
    log('init — names indexed:', Object.keys(maps.byName).length, 'source:', maps.source,
      '| run window.__tepoDesignPatch() to force a pass');
    observeCarts();
    swapDom();
    // On the cart page (or any page that already shows cart lines), do a pass on
    // load to catch items added via a full-page navigation.
    if (Object.keys(maps.byName).length) {
      var isCart = /\/cart(\/|\?|$)/.test(location.pathname + location.search) || !!document.getElementById('main-cart-items');
      if (isCart) { log('cart context detected on load — running a pass'); schedulePatch('init-cart'); }
    }
    // Hook the theme's cart-update event so we refresh right AFTER it opens the
    // drawer with the freshly added (stale) line.
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
          log('theme cart-update event — instant swap + background persist');
          scheduleSwap();
          setTimeout(scheduleSwap, 100);
          setTimeout(function () { patchCart('cart-update', true); }, 200);
          setTimeout(function () { patchCart('cart-update', true); }, 1200);
        });
        log('subscribed to theme cart-update');
      } else {
        log('theme subscribe/PUB_SUB_EVENTS not available; relying on timed passes');
      }
    } catch (e) { log('subscribe failed', e); }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
